import { createContext, useMemo, useContext } from 'react'
import {
  CompareItemsProps,
  CompareItemsSigtest,
  CompareItemsSelectedItem,
  CompareItemsAnalysisMode,
  CompareItemsItem,
  CompareItemsSplitGroup
} from '../types'

export const SigtestContext = createContext<CompareItemsSigtest>(null)

export default function useSigtest(): CompareItemsSigtest {
  const sigtest = useContext(SigtestContext)
  return sigtest
}

export function useSigtestState(
  props: CompareItemsProps,
  {
    selectedItems = [],
    analysisMode
  }: { selectedItems: CompareItemsSelectedItem[]; analysisMode: CompareItemsAnalysisMode }
): CompareItemsSigtest {
  const sigtest: CompareItemsSigtest = useMemo(() => {
    if (!props.enableSigtestAnalysis || !selectedItems.length || 'sigtest' !== analysisMode) {
      return null
    }
    const letters = new Map<number | string, string>()
    const items = new Map<string, CompareItemsItem | CompareItemsSplitGroup['values'][number]>()
    const type = null !== props.splitBy?.ref && 'items' === props.splitBy?.group ? 'splitValue' : 'item'

    // assign letters to selected items
    let nextLetterIndex = 1
    if ('splitValue' === type) {
      const splitByGroup = props.splitGroups?.find?.(group => group.id === props.splitBy.ref)
      if (splitByGroup) {
        splitByGroup.values.forEach((value, valueIndex) => {
          if (!letters.has(valueIndex)) {
            letters.set(valueIndex, getLetter(nextLetterIndex++))
          }
          const letter = letters.get(valueIndex)
          items.set(letter, value)
        })
      }
    } else {
      selectedItems.forEach(item => {
        if (!letters.has(item._index)) {
          letters.set(item._index, getLetter(nextLetterIndex++))
        }
        const letter = letters.get(item._index)
        items.set(letter, item)
      })
    }

    return {
      type,
      letters,
      items
    }
  }, [
    props.enableSigtestAnalysis,
    props.splitBy.ref,
    props.splitBy?.group,
    props.splitGroups,
    selectedItems,
    analysisMode
  ])

  return sigtest
}

function getLetter(index: number) {
  let p = index
  let letter = ''
  let a = 1
  let b = 26
  for (; (p -= a) >= 0; a = b, b *= 26) {
    letter = String.fromCharCode((p % b) / a + 65) + letter
  }
  return letter
}
